/**
 * Conversations read surface, manual sync, and inbox context-panel mutations.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { OutcomeStatus } from '@barel/core';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { getJson, patchJson, postJson } from './_http';
import { conversationKey } from './_keys';

/* ─── Conversations — Wave 5c.1 (read surface; write hooks land in 5c.2) ── */

export interface ConversationListItem {
  id: string;
  accountId: string;
  accountName: string;
  accountInitials: string;
  accountColor: string | null;
  accountMode: 'auto' | 'review' | 'manual';
  accountStatus: 'active' | 'paused' | 'error' | 'limited';
  contactName: string;
  contactInitials: string;
  contactColor: string | null;
  contactRole: string | null;
  contactCompany: string | null;
  contactCompanyLogo: string | null;
  contactCompanyColor: string | null;
  contactAvatarUrl: string | null;
  /** Validated public LinkedIn identifier; never a raw member/profile URN. */
  contactVanitySlug: string | null;
  /** Public LinkedIn URL derived by the API from `contactVanitySlug`. */
  contactProfileUrl: string | null;
  preview: string;
  lastMessageAt: number | null;
  unread: boolean;
  source: 'Waalaxy' | 'LinkedIn' | 'Internal' | 'CSV' | 'Manual' | 'Unknown';
  /** Wave B2 — whether the source pill was auto-detected vs reassigned. */
  sourceDetected: boolean;
  temperature: 'cold' | 'warm' | 'hot' | null;
  offTarget: boolean;
  offTargetReason: string | null;
  pendingActionType: 'meeting' | 'call' | null;
  pendingActionLabel: string | null;
  pendingActionIcon: string | null;
  pendingActionDetail: string | null;
  pendingActionReason: string | null;
  pendingActionSince: number | null;
  pendingActionAutoCompleted: boolean;
  primaryBusinessId: string | null;
  /** Wave B2 — denormalised business name for the list-row chip. */
  primaryBusinessName: string | null;
  leadId: string | null;
  leadPushed: boolean;
  /** Wave B1 — thread completeness: hot-override flag (US-078). */
  isVeryHot: boolean;
  /** Off-target policy resolved from the owning account (`block`/`suggest`/`manual`). */
  offTargetMode: 'block' | 'suggest' | 'manual' | null;
  /** Whether `relancesConfig.enabled` is on for the owning account. */
  relancesEnabled: boolean;
  /** Outcome status when the user has reported one. */
  outcomeStatus: OutcomeStatus | null;
  /** Epoch ms — next queued relance for this conv, or null. */
  relanceScheduledAt: number | null;
  /**
   * Wave B3 (US-089) — true when the AI-side channel-share detector has
   * locked outbound replies. The FE auto-send guard reads this to skip
   * the auto-mode mutation without waiting on a draft round-trip.
   */
  channelShareLocked: boolean;
}

/**
 * Wave B2 — extended envelope returned by `GET /conversations/:id`. Adds
 * the joins the inbox right-rail ContextPanel needs (account subtitle,
 * business description, campaign, top-3 affinity chips). The list endpoint
 * deliberately stays light and returns `ConversationListItem` rows.
 */
export interface ConversationDetail extends ConversationListItem {
  /** Email or vanity-slug subtitle line under the account name. */
  accountSubtitle: string | null;
  /** Plain-text business description for the ContextPanel body. */
  businessDescription: string | null;
  /** Campaign id when the conv came from a campaign run. */
  campaignId: string | null;
  /** Display name for the campaign — `null` for direct outreach. */
  campaignName: string | null;
  /** Top-3 matched businesses by affinity score (0..1). */
  businesses: { id: string; name: string; score: number }[];
  /** Mirror of the owning account's `aiMode`. */
  aiMode: 'auto' | 'review' | 'manual';
}

export interface ConversationsListResponse {
  conversations: ConversationListItem[];
  nextCursor: string | null;
}

export interface ConversationsListFilter {
  accountId?: string | 'all';
  /** Mode-tab filter — `all` is the no-op default. */
  mode?: 'all' | 'action' | 'auto' | 'review' | 'offtarget';
  limit?: number;
}

const conversationsKey = (filter: ConversationsListFilter | undefined) =>
  ['conversations', filter ?? {}] as const;

/**
 * `GET /api/v1/conversations` — infinite-scroll list with optional filters.
 *
 * Returns most-recent-activity-first. Each page is `ConversationsListResponse`;
 * `getNextPageParam` reads `nextCursor` so callers can `fetchNextPage()`.
 */
export function useConversations(
  filter: ConversationsListFilter = {},
  options: { enabled?: boolean } = {},
): UseInfiniteQueryResult<InfiniteData<ConversationsListResponse, string | null>, ApiError> {
  const client = useApiClient();
  return useInfiniteQuery<
    ConversationsListResponse,
    ApiError,
    InfiniteData<ConversationsListResponse, string | null>,
    ReturnType<typeof conversationsKey>,
    string | null
  >({
    queryKey: conversationsKey(filter),
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filter.accountId && filter.accountId !== 'all') {
        params.set('accountId', filter.accountId);
      }
      if (filter.mode && filter.mode !== 'all') {
        params.set('mode', filter.mode);
      }
      if (filter.limit) {
        params.set('limit', String(filter.limit));
      }
      if (pageParam) params.set('cursor', pageParam);
      const qs = params.toString();
      const path = `/api/v1/conversations${qs ? `?${qs}` : ''}`;
      return getJson<ConversationsListResponse>(client, path);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: options.enabled,
    retry: false,
  });
}

/** `GET /api/v1/conversations/:id` — single envelope (Wave B2: detail shape). */
export function useConversation(
  id: string | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<{ conversation: ConversationDetail }, ApiError> {
  const client = useApiClient();
  const enabled = options.enabled ?? Boolean(id);
  return useQuery<{ conversation: ConversationDetail }, ApiError>({
    queryKey: ['conversation', id],
    queryFn: () =>
      getJson<{ conversation: ConversationDetail }>(
        client,
        `/api/v1/conversations/${encodeURIComponent(id ?? '')}`,
      ),
    enabled,
    retry: false,
  });
}

/**
 * Imperative helper — invalidate the conversations list cache.
 *
 * Used by the realtime listener (`message.in` → invalidate so the list
 * re-fetches and re-orders). Exposed as a separate utility so 5c.2 / 5c.3
 * can call it from their own write-side handlers without re-implementing
 * the key shape.
 */
export function invalidateConversations(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['conversations'] });
}

/* ─── Manual conversation sync — Wave 3 (manual refresh button) ─────────── */

/** Variables for {@link useSyncConversations}. Omit `accountId` to sync all. */
export interface SyncConversationsInput {
  /** Specific account to sync, or omit/empty for "all visible accounts". */
  accountId?: string;
}

/** Acknowledgement returned by `POST /api/v1/conversations/sync`. */
export interface SyncConversationsAck {
  ok: true;
}

/**
 * `POST /api/v1/conversations/sync` — kicks off an out-of-band poll for new
 * messages on the targeted account(s). The server emits a realtime
 * `conversations.sync-requested` event; the extension consumes it and runs
 * the bounded Voyager poll. Caller-side cooldown + disabled-while-limited
 * is enforced in the inbox UI (the API also rate-limits to 5 / 5min /
 * (user, account)).
 */
export function useSyncConversations(): UseMutationResult<
  SyncConversationsAck,
  ApiError,
  SyncConversationsInput
> {
  const client = useApiClient();
  return useMutation<SyncConversationsAck, ApiError, SyncConversationsInput>({
    mutationFn: (input) =>
      postJson<SyncConversationsAck>(client, '/api/v1/conversations/sync', input ?? {}),
  });
}

// Conversations writes — 5c.2 + 5c.3 implementations follow below.

// `ThreadMessage` mirrors the `messages` row shape returned by
// `GET /api/v1/conversations/:id/messages`. Kept here (rather than in a
// separate types file) so consumers can import it alongside the hook
// without dragging in another package layer. EPIC-05 US-073.
export type ThreadMessageRole = 'in' | 'out' | 'system';

export type ThreadMessageOutboundSource =
  | 'ai_auto'
  | 'ai_review'
  | 'manual_app'
  | 'manual_linkedin'
  | 'campaign';

/** SPEC-SEND-TRANSPORT §6 — delivery truth for an `out` message row. */
export type ThreadMessageDeliveryState = 'pending' | 'sent' | 'failed';

export interface ThreadMessage {
  id: string;
  conversationId: string;
  role: ThreadMessageRole;
  body: string;
  systemDetail: string | null;
  outboundSource: ThreadMessageOutboundSource | null;
  /** Epoch milliseconds — server normalises Date → number. */
  sentAt: number | null;
  receivedAt: number | null;
  createdAt: number | null;
  /**
   * SPEC-SEND-TRANSPORT §6 — delivery truth. `null` = no tracking (inbound /
   * system rows, or `out` rows written before the delivery-state migration).
   * Consumers treat `null` the same as `sent` for `out` rows.
   */
  deliveryState: ThreadMessageDeliveryState | null;
  /** Reason code when `deliveryState === 'failed'` (e.g. `rate-limited`). */
  deliveryError: string | null;
}

export interface ConversationMessagesPage {
  messages: ThreadMessage[];
  nextCursor: string | null;
}

export const conversationMessagesQueryKey = (conversationId: string | undefined) =>
  ['conversation-messages', conversationId ?? ''] as const;

/**
 * `GET /api/v1/conversations/:id/messages` — cursor-paginated thread
 * history (EPIC-05 US-073). Each page is `ConversationMessagesPage`;
 * `getNextPageParam` reads `nextCursor` so callers can `fetchNextPage()`.
 *
 * Pages return ASC by `createdAt` so consumers can render bubbles in
 * chronological order without sorting client-side.
 */
export function useConversationMessages(
  conversationId: string | undefined,
  options: { enabled?: boolean } = {},
): UseInfiniteQueryResult<InfiniteData<ConversationMessagesPage, string | null>, ApiError> {
  const client = useApiClient();
  const enabled = (options.enabled ?? true) && Boolean(conversationId);
  return useInfiniteQuery<
    ConversationMessagesPage,
    ApiError,
    InfiniteData<ConversationMessagesPage, string | null>,
    ReturnType<typeof conversationMessagesQueryKey>,
    string | null
  >({
    queryKey: conversationMessagesQueryKey(conversationId),
    initialPageParam: null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (pageParam) params.set('cursor', pageParam);
      const qs = params.toString();
      const path = `/api/v1/conversations/${encodeURIComponent(conversationId ?? '')}/messages${
        qs ? `?${qs}` : ''
      }`;
      return getJson<ConversationMessagesPage>(client, path);
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    retry: false,
  });
}

export interface MarkConversationReadInput {
  conversationId: string;
}

export interface MarkConversationReadResponse {
  conversation: { id: string; unread: false };
  alreadyRead: boolean;
}

interface MarkConversationReadContext {
  previousLists: [readonly unknown[], InfiniteData<ConversationsListResponse, string | null>][];
  previousDetail: { conversation: ConversationDetail } | undefined;
}

function markReadInListData(
  data: InfiniteData<ConversationsListResponse, string | null>,
  conversationId: string,
): InfiniteData<ConversationsListResponse, string | null> {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      conversations: page.conversations.map((conversation) =>
        conversation.id === conversationId && conversation.unread
          ? { ...conversation, unread: false }
          : conversation,
      ),
    })),
  };
}

/**
 * `POST /api/v1/conversations/:id/read` — mark an owned conversation read.
 *
 * Every cached infinite-list variant and the selected detail envelope are
 * updated optimistically so the row badge/sidebar dot clear immediately.
 * Failed writes restore the exact snapshots; settled writes refetch because
 * a concurrent inbound event may have made the conversation unread again.
 */
export function useMarkConversationRead(): UseMutationResult<
  MarkConversationReadResponse,
  ApiError,
  MarkConversationReadInput,
  MarkConversationReadContext
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    MarkConversationReadResponse,
    ApiError,
    MarkConversationReadInput,
    MarkConversationReadContext
  >({
    mutationFn: ({ conversationId }) =>
      postJson<MarkConversationReadResponse>(
        client,
        `/api/v1/conversations/${encodeURIComponent(conversationId)}/read`,
        {},
      ),
    onMutate: async ({ conversationId }) => {
      await Promise.all([
        qc.cancelQueries({ queryKey: ['conversations'] }),
        qc.cancelQueries({ queryKey: conversationKey(conversationId) }),
      ]);

      const previousLists = qc
        .getQueriesData<InfiniteData<ConversationsListResponse, string | null>>({
          queryKey: ['conversations'],
        })
        .filter(
          (
            entry,
          ): entry is [
            readonly unknown[],
            InfiniteData<ConversationsListResponse, string | null>,
          ] => entry[1] !== undefined,
        );
      for (const [key, data] of previousLists) {
        qc.setQueryData(key, markReadInListData(data, conversationId));
      }

      const detailKey = conversationKey(conversationId);
      const previousDetail = qc.getQueryData<{ conversation: ConversationDetail }>(detailKey);
      if (previousDetail) {
        qc.setQueryData<{ conversation: ConversationDetail }>(detailKey, {
          conversation: { ...previousDetail.conversation, unread: false },
        });
      }
      return { previousLists, previousDetail };
    },
    onError: (_error, { conversationId }, context) => {
      for (const [key, data] of context?.previousLists ?? []) {
        qc.setQueryData(key, data);
      }
      if (context?.previousDetail) {
        qc.setQueryData(conversationKey(conversationId), context.previousDetail);
      }
    },
    onSuccess: (_response, { conversationId }) => {
      const detailKey = conversationKey(conversationId);
      qc.setQueryData<{ conversation: ConversationDetail } | undefined>(detailKey, (current) =>
        current ? { conversation: { ...current.conversation, unread: false } } : current,
      );
    },
    onSettled: (_data, _error, { conversationId }) => {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      void qc.invalidateQueries({ queryKey: conversationKey(conversationId) });
    },
  });
}

/* ─── Inbox context-panel mutations — Wave 5c Agent 5c.3 (FULLY IMPLEMENTED) ─ */

/**
 * Source-pill taxonomy mirrored from `@barel/core` (kept inline to avoid
 * pulling the whole core package into the bundle for one literal type).
 */
export type ConversationSourcePill =
  | 'Waalaxy'
  | 'LinkedIn'
  | 'Internal'
  | 'CSV'
  | 'Manual'
  | 'Unknown';

export interface ConversationContextRow {
  id: string;
  sourcePill: ConversationSourcePill;
  sourceDetected: boolean;
  /** Loose passthrough — server returns the full row; consumers narrow as needed. */
  [key: string]: unknown;
}

export interface ReassignSourceVars {
  /** Target conversation. */
  conversationId: string;
  /** New source pill (taxonomy-bounded). */
  sourcePill: ConversationSourcePill;
  /** Optional override; defaults to `false` per US-082 ("Assigned manually"). */
  sourceDetected?: boolean;
}

export interface ManualReplyVars {
  conversationId: string;
  body: string;
  attachments?: { r2Key: string; filename: string; mimeType: string; size: number }[];
  /**
   * SPEC-SEND-TRANSPORT §1.4 idempotency key — same shape as
   * `SendMessageInput.clientToken` (`/^[A-Za-z0-9_-]{1,64}$/`). Optional so
   * existing callers keep working; the route derives the commandId from it
   * when present so a retried double-submit doesn't double-send.
   */
  clientToken?: string;
}

export interface ManualReplyMessage {
  id: string;
  conversationId: string;
  role: 'out';
  body: string;
  outboundSource: 'manual_app';
  sentAt: number | null;
  createdAt: number;
  [key: string]: unknown;
}

const conversationsListKey = ['conversations'] as const;

/**
 * `PATCH /api/v1/conversations/:id` — reassign source pill (US-082). The
 * server flips `sourceDetected` to `false` automatically when only
 * `sourcePill` is supplied; pass `sourceDetected: true` explicitly to
 * preserve the auto-detected state.
 */
export function useReassignSource(): UseMutationResult<
  { conversation: ConversationContextRow },
  ApiError,
  ReassignSourceVars
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ conversation: ConversationContextRow }, ApiError, ReassignSourceVars>({
    mutationFn: (vars) =>
      patchJson<{ conversation: ConversationContextRow }>(
        client,
        `/api/v1/conversations/${encodeURIComponent(vars.conversationId)}`,
        {
          sourcePill: vars.sourcePill,
          ...(vars.sourceDetected !== undefined ? { sourceDetected: vars.sourceDetected } : {}),
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: conversationKey(vars.conversationId) });
      void qc.invalidateQueries({ queryKey: conversationsListKey });
    },
  });
}

/**
 * `POST /api/v1/conversations/:id/manual-reply` — append a hand-written
 * outbound message (US-081). Outstanding AI drafts are server-side
 * superseded; consumers should invalidate the conversation thread cache
 * key (`['conversation', id, 'messages']`) — 5c.2 owns that key shape so
 * we invalidate the broad `['conversation', id]` prefix here.
 */
export function useManualReply(): UseMutationResult<
  { message: ManualReplyMessage },
  ApiError,
  ManualReplyVars
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ message: ManualReplyMessage }, ApiError, ManualReplyVars>({
    mutationFn: (vars) =>
      postJson<{ message: ManualReplyMessage }>(
        client,
        `/api/v1/conversations/${encodeURIComponent(vars.conversationId)}/manual-reply`,
        {
          body: vars.body,
          attachments: vars.attachments ?? [],
          ...(vars.clientToken ? { clientToken: vars.clientToken } : {}),
        },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: conversationKey(vars.conversationId) });
      void qc.invalidateQueries({ queryKey: conversationsListKey });
    },
  });
}
